/**
 * BusinessSettingsHub — the vendor-context "profile settings" surface
 * rendered on AccountPage when active_context === "business".
 *
 * Mirrors the Agency Settings layout: a left-hand menu of product
 * sections + a right-hand editor pane. Each section surfaces the
 * per-business settings that live in the DB today (business profile,
 * restaurant settings, QR codes, directory, meetups, streaming, chat).
 *
 * Data-driven SECTIONS array → adding a new product = one entry.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Building2, UtensilsCrossed, QrCode, BookOpen,
  Users, Radio, MessageCircle, LogOut, Save, ArrowRight, ExternalLink,
  UserRound, Store, UserPlus, Trash2, Copy, Plus, Play, Mail,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import BusinessDashboard from "@/components/BusinessDashboard";

const SECTIONS = [
  { key: "overview",   label: "Overview",         icon: LayoutDashboard, group: "Business" },
  { key: "profile",    label: "Business Profile", icon: Building2,       group: "Business" },
  { key: "team",       label: "Team",             icon: UserPlus,        group: "Business" },
  { key: "restaurant", label: "Restaurant",       icon: UtensilsCrossed, group: "Products" },
  { key: "qr",         label: "QR Codes",         icon: QrCode,          group: "Products" },
  { key: "directory",  label: "Directory",        icon: BookOpen,        group: "Products" },
  { key: "meetups",    label: "Meetups",          icon: Users,           group: "Products" },
  { key: "streaming",  label: "Streaming",        icon: Radio,           group: "Products" },
  { key: "chat",       label: "Chat",             icon: MessageCircle,   group: "Products" },
];

export const BusinessSettingsHub = ({ meState, onLogout, onSwitchPersonal }) => {
  const [active, setActive] = useState(() => {
    try { return window.localStorage.getItem("biz-settings-section") || "overview"; }
    catch (_e) { return "overview"; }
  });

  function pick(key) {
    setActive(key);
    try { window.localStorage.setItem("biz-settings-section", key); } catch (_e) { /* ignore */ }
  }

  const groups = [...new Set(SECTIONS.map((s) => s.group))];

  return (
    <div
      data-testid="business-settings-hub"
      className="bg-white border border-[#E8E5DF] rounded-2xl overflow-hidden grid grid-cols-1 lg:grid-cols-[240px_1fr]"
    >
      {/* ── Left menu ── */}
      <aside
        data-testid="biz-settings-nav"
        className="border-b lg:border-b-0 lg:border-r border-[#E8E5DF] bg-[#FBF9F4] flex flex-col"
      >
        <div className="p-5 border-b border-[#E8E5DF] hidden lg:block">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#5A7247]">
            Business settings
          </p>
          <p className="font-serif-display text-lg mt-1 truncate">
            {meState?.business_name || "Your business"}
          </p>
        </div>

        {/* Section list — horizontal scroll on mobile, vertical on desktop */}
        <nav className="flex lg:flex-col gap-1 p-2 lg:p-3 overflow-x-auto lg:overflow-visible">
          {groups.map((grp) => (
            <div key={grp} className="contents lg:block">
              <p className="hidden lg:block px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.15em] font-bold text-[#9BA19C]">
                {grp}
              </p>
              {SECTIONS.filter((s) => s.group === grp).map((s) => {
                const Icon = s.icon;
                const isActive = active === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`biz-settings-tab-${s.key}`}
                    onClick={() => pick(s.key)}
                    className={`shrink-0 lg:w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                      isActive
                        ? "bg-[#C05746]/10 text-[#C05746] font-medium"
                        : "text-[#121614] hover:bg-[#F0EBE1]"
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                    <span className="whitespace-nowrap lg:truncate">{s.label}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Footer — prominent sign out + switch to personal */}
        <div className="mt-auto p-3 border-t border-[#E8E5DF] space-y-2">
          {onSwitchPersonal && (
            <button
              type="button"
              data-testid="biz-settings-switch-personal"
              onClick={onSwitchPersonal}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-[#121614] hover:bg-[#F0EBE1] transition-colors"
            >
              <UserRound className="h-4 w-4" strokeWidth={1.5} />
              Switch to personal
            </button>
          )}
          <Button
            type="button"
            data-testid="biz-settings-logout"
            onClick={onLogout}
            className="w-full rounded-full bg-[#C05746] hover:bg-[#A64A3C] text-white gap-2"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.75} />
            Sign out
          </Button>
        </div>
      </aside>

      {/* ── Right panel ── */}
      <main data-testid="biz-settings-panel" className="p-5 md:p-8 min-h-[480px]">
        {active === "overview"   && <OverviewPanel />}
        {active === "profile"    && <ProfilePanel />}
        {active === "team"       && <MembersPanel />}
        {active === "restaurant" && <RestaurantPanel />}
        {active === "qr"         && <QRPanel />}
        {active === "directory"  && <DirectoryPanel meState={meState} />}
        {active === "meetups"    && <MeetupsPanel />}
        {active === "streaming"  && <StreamingPanel />}
        {active === "chat"       && <ChatPanel />}
      </main>
    </div>
  );
};

/* ── Shared bits ──────────────────────────────────────────────────── */
function PanelHeader({ title, subtitle }) {
  return (
    <div className="mb-6">
      <h2 className="font-serif-display text-2xl text-[#121614]">{title}</h2>
      {subtitle && <p className="text-sm text-[#5C625E] mt-1">{subtitle}</p>}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[#3C423F]">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-[#5C625E] mt-1">{hint}</p>}
    </div>
  );
}

function LinkCard({ to, icon: Icon, title, desc, cta = "Open", external = false }) {
  const inner = (
    <>
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-[#F1F4EA] text-[#5A7247] flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <p className="font-medium text-[#121614]">{title}</p>
          <p className="text-xs text-[#5C625E]">{desc}</p>
        </div>
      </div>
      <span className="inline-flex items-center gap-1 text-sm font-medium text-[#C05746] shrink-0">
        {cta}
        {external
          ? <ExternalLink className="h-4 w-4" strokeWidth={1.75} />
          : <ArrowRight className="h-4 w-4" strokeWidth={1.75} />}
      </span>
    </>
  );
  const cls =
    "flex items-center justify-between gap-4 p-4 rounded-xl border border-[#E8E5DF] hover:border-[#C05746] hover:bg-[#FBF9F4] transition-colors";
  return external
    ? <a href={to} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
    : <Link to={to} className={cls}>{inner}</Link>;
}

/* ── Live branding preview ────────────────────────────────────────── */
function BrandingPreview({ biz }) {
  const name = biz.business_name || biz.name || "Your business";
  const headline = biz.hero_headline || name;
  return (
    <div className="lg:sticky lg:top-6 h-fit" data-testid="biz-profile-preview">
      <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#9BA19C] mb-2">
        Live preview
      </p>
      <div className="rounded-2xl overflow-hidden border border-[#E8E5DF] bg-white shadow-sm">
        <div
          className="relative h-40 bg-[#1A1E1B] bg-cover bg-center"
          style={biz.hero_image_url ? { backgroundImage: `url("${biz.hero_image_url}")` } : undefined}
        >
          {!biz.hero_image_url && (
            <div className="absolute inset-0 flex items-center justify-center text-[11px] text-[#8A908B]">
              Hero image appears here
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
          <div className="absolute bottom-3 left-4 right-4 text-white">
            <p className="font-serif-display text-lg leading-tight drop-shadow" data-testid="biz-preview-headline">
              {headline}
            </p>
            {biz.hero_subheadline && (
              <p className="text-[11px] opacity-90 mt-0.5 line-clamp-2">{biz.hero_subheadline}</p>
            )}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-center gap-3">
            {biz.logo_url ? (
              <img
                src={biz.logo_url}
                alt=""
                className="h-11 w-11 rounded-full object-cover border border-[#E8E5DF] shrink-0"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                data-testid="biz-preview-logo"
              />
            ) : (
              <span className="h-11 w-11 rounded-full bg-[#F1F4EA] text-[#5A7247] flex items-center justify-center font-bold shrink-0">
                {name.slice(0, 1).toUpperCase()}
              </span>
            )}
            <div className="min-w-0">
              <p className="font-medium text-[#121614] truncate">{name}</p>
              <p className="text-xs text-[#5C625E] truncate" data-testid="biz-preview-tagline">
                {biz.tagline || "Your tagline shows here"}
              </p>
            </div>
          </div>
          {biz.cta_label && (
            <div className="mt-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#C05746] text-white text-xs font-medium px-4 py-2">
                {biz.cta_label}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </span>
            </div>
          )}
        </div>
      </div>
      <p className="text-[11px] text-[#9BA19C] mt-2">Updates as you type — this is how customers see your storefront.</p>
    </div>
  );
}

/* ── Overview ─────────────────────────────────────────────────────── */
function OverviewPanel() {
  return (
    <div data-testid="biz-panel-overview">
      <PanelHeader
        title="Overview"
        subtitle="Today's trading snapshot for your business."
      />
      <BusinessDashboard />
    </div>
  );
}

/* ── Business Profile (GET /businesses/current · PATCH /admin/business) */
const SOCIAL_FIELDS = [
  ["instagram", "Instagram"], ["facebook", "Facebook"], ["tiktok", "TikTok"],
  ["youtube", "YouTube"], ["linkedin", "LinkedIn"],
];

function ProfilePanel() {
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/businesses/current");
        if (!cancelled) setBiz(data || {});
      } catch (e) {
        if (!cancelled) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
          setBiz({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function set(k, v) { setBiz((b) => ({ ...b, [k]: v })); }
  function setSocial(k, v) {
    setBiz((b) => ({ ...b, socials: { ...(b.socials || {}), [k]: v } }));
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        tagline: biz.tagline || "",
        hero_headline: biz.hero_headline || "",
        hero_subheadline: biz.hero_subheadline || "",
        cta_label: biz.cta_label || "",
        cta_link: biz.cta_link || "",
        logo_url: biz.logo_url || "",
        hero_image_url: biz.hero_image_url || "",
        socials: biz.socials || {},
      };
      const { data } = await api.patch("/admin/business", payload);
      setBiz((b) => ({ ...b, ...data }));
      toast.success("Business profile saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p data-testid="biz-panel-profile-loading" className="text-[#5C625E]">Loading profile…</p>;
  }

  return (
    <div data-testid="biz-panel-profile" className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
      <div>
      <PanelHeader
        title="Business Profile"
        subtitle="Your public identity across the suite — storefront hero, tagline and socials."
      />
      <div className="space-y-5">
        <Field label="Business name" hint="Managed by your agency — contact them to change it.">
          <Input
            data-testid="biz-profile-name"
            value={biz.business_name || biz.name || ""}
            disabled
            className="mt-2 rounded-xl bg-[#F3F0EA] text-[#5C625E]"
          />
        </Field>
        <Field label="Tagline">
          <Input data-testid="biz-profile-tagline" value={biz.tagline || ""}
            onChange={(e) => set("tagline", e.target.value)}
            placeholder="e.g. Wood-fired sourdough, baked fresh daily"
            className="mt-2 rounded-xl" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Hero headline">
            <Input data-testid="biz-profile-hero-headline" value={biz.hero_headline || ""}
              onChange={(e) => set("hero_headline", e.target.value)}
              className="mt-2 rounded-xl" />
          </Field>
          <Field label="Hero sub-headline">
            <Input data-testid="biz-profile-hero-subheadline" value={biz.hero_subheadline || ""}
              onChange={(e) => set("hero_subheadline", e.target.value)}
              className="mt-2 rounded-xl" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Call-to-action label">
            <Input data-testid="biz-profile-cta-label" value={biz.cta_label || ""}
              onChange={(e) => set("cta_label", e.target.value)}
              placeholder="e.g. Book a table" className="mt-2 rounded-xl" />
          </Field>
          <Field label="Call-to-action link">
            <Input data-testid="biz-profile-cta-link" value={biz.cta_link || ""}
              onChange={(e) => set("cta_link", e.target.value)}
              placeholder="/book" className="mt-2 rounded-xl" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Logo URL">
            <Input data-testid="biz-profile-logo" value={biz.logo_url || ""}
              onChange={(e) => set("logo_url", e.target.value)}
              className="mt-2 rounded-xl" />
          </Field>
          <Field label="Hero image URL">
            <Input data-testid="biz-profile-hero-image" value={biz.hero_image_url || ""}
              onChange={(e) => set("hero_image_url", e.target.value)}
              className="mt-2 rounded-xl" />
          </Field>
        </div>

        <div>
          <Label className="text-[#3C423F]">Social links</Label>
          <div className="mt-2 grid sm:grid-cols-2 gap-3">
            {SOCIAL_FIELDS.map(([k, lbl]) => (
              <Input
                key={k}
                data-testid={`biz-profile-social-${k}`}
                value={(biz.socials || {})[k] || ""}
                onChange={(e) => setSocial(k, e.target.value)}
                placeholder={lbl}
                className="rounded-xl"
              />
            ))}
          </div>
        </div>

        <Button type="button" data-testid="biz-profile-save" onClick={save} disabled={saving}
          className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6 h-11">
          <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
          {saving ? "Saving…" : "Save profile"}
        </Button>
      </div>
      </div>
      <BrandingPreview biz={biz} />
    </div>
  );
}

/* ── Restaurant (GET /settings · PATCH /settings) ─────────────────── */
function RestaurantPanel() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setS(data || {});
      } catch (e) {
        if (!cancelled) { setS({}); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function set(k, v) { setS((p) => ({ ...p, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        restaurant_name: s.restaurant_name || "",
        address: s.address || "",
        phone: s.phone || "",
        logo_url: s.logo_url || "",
        hero_image_url: s.hero_image_url || "",
        prepay_discount_percent: Number(s.prepay_discount_percent) || 0,
      };
      const { data } = await api.patch("/settings", payload);
      setS((p) => ({ ...p, ...data }));
      toast.success("Restaurant settings saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p data-testid="biz-panel-restaurant-loading" className="text-[#5C625E]">Loading restaurant settings…</p>;
  }

  return (
    <div data-testid="biz-panel-restaurant" className="max-w-2xl">
      <PanelHeader
        title="Restaurant"
        subtitle="Venue details used on your storefront, bookings and the ordering flow."
      />
      <div className="space-y-5">
        <Field label="Restaurant / venue name">
          <Input data-testid="biz-rest-name" value={s.restaurant_name || ""}
            onChange={(e) => set("restaurant_name", e.target.value)}
            className="mt-2 rounded-xl" />
        </Field>
        <Field label="Address">
          <Textarea data-testid="biz-rest-address" value={s.address || ""}
            onChange={(e) => set("address", e.target.value)} rows={2}
            className="mt-2 rounded-xl" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Phone">
            <Input data-testid="biz-rest-phone" value={s.phone || ""}
              onChange={(e) => set("phone", e.target.value)} className="mt-2 rounded-xl" />
          </Field>
          <Field label="Pre-pay discount %" hint="0–50% off when customers pay ahead.">
            <Input data-testid="biz-rest-prepay" type="number" min="0" max="50" step="1"
              value={s.prepay_discount_percent ?? 0}
              onChange={(e) => set("prepay_discount_percent", e.target.value)}
              className="mt-2 rounded-xl" />
          </Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Logo URL">
            <Input data-testid="biz-rest-logo" value={s.logo_url || ""}
              onChange={(e) => set("logo_url", e.target.value)} className="mt-2 rounded-xl" />
          </Field>
          <Field label="Hero image URL">
            <Input data-testid="biz-rest-hero" value={s.hero_image_url || ""}
              onChange={(e) => set("hero_image_url", e.target.value)} className="mt-2 rounded-xl" />
          </Field>
        </div>

        <Button type="button" data-testid="biz-rest-save" onClick={save} disabled={saving}
          className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6 h-11">
          <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
          {saving ? "Saving…" : "Save restaurant settings"}
        </Button>

        <div className="pt-4 border-t border-[#E8E5DF] space-y-2">
          <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#9BA19C]">Manage</p>
          <LinkCard to="/admin/menu" icon={UtensilsCrossed} title="Menu" desc="Dishes, categories, prices & photos" cta="Edit" />
          <LinkCard to="/admin/bookings" icon={BookOpen} title="Bookings" desc="Table reservations & availability" cta="Open" />
          <LinkCard to="/admin" icon={Store} title="Full restaurant console" desc="Orders, floorplan, print, marketing & more" cta="Open" />
        </div>
      </div>
    </div>
  );
}

/* ── QR Codes ─────────────────────────────────────────────────────── */
function QRPanel() {
  const [count, setCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    api.get("/qr/codes").then((r) => {
      if (!cancelled) setCount(Array.isArray(r.data) ? r.data.length : (r.data?.codes?.length ?? null));
    }).catch(() => { if (!cancelled) setCount(null); });
    return () => { cancelled = true; };
  }, []);
  return (
    <div data-testid="biz-panel-qr" className="max-w-2xl">
      <PanelHeader
        title="QR Codes"
        subtitle="Dynamic QR codes for tables, contact capture, menus and campaigns."
      />
      {count != null && (
        <p data-testid="biz-qr-count" className="mb-4 text-sm text-[#5C625E]">
          You currently have <strong className="text-[#121614]">{count}</strong> QR code{count === 1 ? "" : "s"} set up.
        </p>
      )}
      <div className="space-y-2">
        <LinkCard to="/account/qr-codes" icon={QrCode} title="Manage QR codes" desc="Create, edit and download your codes & company profile" cta="Open" />
      </div>
    </div>
  );
}

/* ── Directory (inline lead-routing + whitelabel editor) ──────────── */
const BIZ_TO_SLUG = {
  biz_taxassist_oxley_4075: "taxassist-accountants-oxley-4075",
};

function DirectoryPanel({ meState }) {
  const slug = BIZ_TO_SLUG[meState?.business_id] || "";
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(!!slug);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    recipient_email: "", cc_emails: "", reply_to: "",
    subject_prefix: "", hide_hustle_header: false,
  });

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/directory/vendor/${slug}/config`);
        if (cancelled) return;
        setCfg(data);
        setForm((f) => ({
          ...f,
          subject_prefix: data?.contact_form?.subject_prefix || `[${data?.name || "Directory"}]`,
          hide_hustle_header: !!data?.hide_hustle_header,
        }));
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    if (!form.recipient_email.trim()) { toast.error("Add a recipient email"); return; }
    setSaving(true);
    try {
      const payload = {
        recipient_email: form.recipient_email.trim(),
        cc_emails: form.cc_emails.split(",").map((s) => s.trim()).filter(Boolean),
        reply_to: form.reply_to.trim() || undefined,
        subject_prefix: form.subject_prefix.trim() || undefined,
        hide_hustle_header: form.hide_hustle_header,
      };
      await api.patch(`/directory/vendor/${slug}/settings`, payload);
      toast.success("Directory settings saved — new leads route here");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }

  if (!slug) {
    return (
      <div data-testid="biz-panel-directory" className="max-w-2xl">
        <PanelHeader
          title="Directory"
          subtitle="Your listing across the Hustle directory — enquiry routing and whitelabel options."
        />
        <div
          data-testid="biz-directory-not-linked"
          className="rounded-xl border border-dashed border-[#E8E5DF] bg-[#FBF9F4] p-6 text-sm text-[#5C625E]"
        >
          This business isn't linked to a directory listing yet. Once your agency
          publishes your listing, you'll route enquiries and toggle whitelabel mode
          right here — no need to leave this page.
        </div>
      </div>
    );
  }

  if (loading) {
    return <p data-testid="biz-panel-directory-loading" className="text-[#5C625E]">Loading directory settings…</p>;
  }

  return (
    <div data-testid="biz-panel-directory" className="max-w-2xl">
      <PanelHeader
        title="Directory"
        subtitle={<>Route enquiries from your <a className="text-[#C05746] underline" href={`https://hustledirectory.au/pub/${slug}`} target="_blank" rel="noreferrer">live listing ↗</a> and control whitelabel chrome.</>}
      />
      <div className="space-y-5">
        <Field label="Lead recipient email" hint="Every directory enquiry is emailed here and saved to your leads.">
          <Input data-testid="biz-dir-recipient" type="email" value={form.recipient_email}
            onChange={(e) => set("recipient_email", e.target.value)}
            placeholder="you@yourdomain.com" className="mt-2 rounded-xl" />
        </Field>
        <Field label="CC emails" hint="Comma-separated.">
          <Input data-testid="biz-dir-cc" value={form.cc_emails}
            onChange={(e) => set("cc_emails", e.target.value)}
            placeholder="ops@example.com, admin@example.com" className="mt-2 rounded-xl" />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Reply-to">
            <Input data-testid="biz-dir-replyto" type="email" value={form.reply_to}
              onChange={(e) => set("reply_to", e.target.value)} className="mt-2 rounded-xl" />
          </Field>
          <Field label="Subject prefix">
            <Input data-testid="biz-dir-subject" value={form.subject_prefix}
              onChange={(e) => set("subject_prefix", e.target.value)} maxLength={80}
              className="mt-2 rounded-xl" />
          </Field>
        </div>
        <label className="flex items-start gap-3 p-4 rounded-xl bg-[#FBF9F4] border border-[#E8E5DF] cursor-pointer">
          <input type="checkbox" data-testid="biz-dir-whitelabel" checked={form.hide_hustle_header}
            onChange={(e) => set("hide_hustle_header", e.target.checked)} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">
            <span className="font-medium text-[#121614]">Whitelabel mode</span>
            <span className="block text-xs text-[#5C625E] mt-0.5">Hide the Hustle Directory header &amp; footer so your listing looks fully self-branded on your own domain.</span>
          </span>
        </label>
        <div className="flex flex-wrap items-center gap-4">
          <Button type="button" data-testid="biz-dir-save" onClick={save} disabled={saving}
            className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6 h-11">
            <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
            {saving ? "Saving…" : "Save directory settings"}
          </Button>
          <Link to="/account/vendor-settings" className="text-sm text-[#5C625E] underline">Preview form fields</Link>
        </div>
      </div>
    </div>
  );
}

/* ── Meetups (inline venue overrides editor) ──────────────────────── */
function MeetupsPanel() {
  const [venueSlug, setVenueSlug] = useState(() => {
    try { return localStorage.getItem("hp_venue_slug") || "demo-trattoria"; }
    catch (_e) { return "demo-trattoria"; }
  });
  const [catalog, setCatalog] = useState(null);
  const [config, setConfig] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    event_type: "birthday", deposit_pct_override: "",
    min_guest_count: "", max_guest_count: "", blocked_dates: "", notes: "",
  });

  useEffect(() => {
    api.get("/events/catalog").then((r) => setCatalog(r.data)).catch(() => setCatalog(null));
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/admin/venues/${encodeURIComponent(venueSlug)}/config`);
      setConfig(data?.items || []);
    } catch (_e) { setConfig([]); }
  }, [venueSlug]);

  useEffect(() => {
    try { localStorage.setItem("hp_venue_slug", venueSlug); } catch (_e) { /* ignore */ }
    loadConfig();
  }, [venueSlug, loadConfig]);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    setSaving(true);
    try {
      const payload = {
        event_type: form.event_type,
        deposit_pct_override: form.deposit_pct_override ? parseFloat(form.deposit_pct_override) : null,
        min_guest_count: form.min_guest_count ? parseInt(form.min_guest_count, 10) : null,
        max_guest_count: form.max_guest_count ? parseInt(form.max_guest_count, 10) : null,
        blocked_dates: form.blocked_dates.split(",").map((s) => s.trim()).filter(Boolean),
        notes: form.notes,
      };
      await api.post(`/events/admin/venues/${encodeURIComponent(venueSlug)}/config`, payload);
      toast.success("Meetup settings saved");
      await loadConfig();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }

  const types = catalog?.event_types || [];

  return (
    <div data-testid="biz-panel-meetups" className="max-w-2xl">
      <PanelHeader
        title="Meetups"
        subtitle="Set deposit rules, guest limits and blocked dates for the events your venue hosts."
      />
      <div className="space-y-5">
        <Field label="Venue slug" hint="The venue these settings apply to.">
          <Input data-testid="biz-meetup-venue-slug" value={venueSlug}
            onChange={(e) => setVenueSlug(e.target.value)} className="mt-2 rounded-xl" />
        </Field>

        <div className="rounded-xl border border-[#E8E5DF] p-4 space-y-4 bg-[#FBF9F4]">
          <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#9BA19C]">Add / update an override</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Event type">
              <select data-testid="biz-meetup-event-type" value={form.event_type}
                onChange={(e) => set("event_type", e.target.value)}
                className="mt-2 w-full rounded-xl border border-[#E8E5DF] bg-white px-3 h-10 text-sm">
                {types.length === 0 && <option value="birthday">Birthday</option>}
                {types.map((t) => (
                  <option key={t.code} value={t.code}>{t.icon ? `${t.icon} ` : ""}{t.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Deposit % override" hint="Blank = catalog default.">
              <Input data-testid="biz-meetup-deposit" type="number" min="0" max="100"
                value={form.deposit_pct_override}
                onChange={(e) => set("deposit_pct_override", e.target.value)} className="mt-2 rounded-xl" />
            </Field>
            <Field label="Min guests">
              <Input data-testid="biz-meetup-min" type="number" min="1" value={form.min_guest_count}
                onChange={(e) => set("min_guest_count", e.target.value)} className="mt-2 rounded-xl" />
            </Field>
            <Field label="Max guests">
              <Input data-testid="biz-meetup-max" type="number" min="1" value={form.max_guest_count}
                onChange={(e) => set("max_guest_count", e.target.value)} className="mt-2 rounded-xl" />
            </Field>
          </div>
          <Field label="Blocked dates" hint="Comma-separated YYYY-MM-DD.">
            <Input data-testid="biz-meetup-blocked" value={form.blocked_dates}
              onChange={(e) => set("blocked_dates", e.target.value)}
              placeholder="2027-12-25, 2027-12-26" className="mt-2 rounded-xl" />
          </Field>
          <Field label="Notes">
            <Textarea data-testid="biz-meetup-notes" value={form.notes}
              onChange={(e) => set("notes", e.target.value)} rows={2} className="mt-2 rounded-xl" />
          </Field>
          <Button type="button" data-testid="biz-meetup-save" onClick={save} disabled={saving}
            className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6 h-11">
            <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
            {saving ? "Saving…" : "Save override"}
          </Button>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#9BA19C] mb-2">Current overrides</p>
          {config.length === 0 ? (
            <p className="text-sm text-[#5C625E]" data-testid="biz-meetup-no-overrides">No overrides yet — catalog defaults apply.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="biz-meetup-overrides">
              {config.map((c, i) => (
                <div key={c.event_type || i} data-testid={`biz-meetup-ovr-${c.event_type}`}
                  className="rounded-xl border border-[#E8E5DF] p-4 bg-white">
                  <p className="font-medium text-[#121614] text-sm mb-1 capitalize">{c.event_type}</p>
                  <div className="text-xs text-[#5C625E] space-y-0.5">
                    {c.deposit_pct_override != null && <div>Deposit: <strong>{c.deposit_pct_override}%</strong></div>}
                    {c.min_guest_count != null && <div>Min guests: {c.min_guest_count}</div>}
                    {c.max_guest_count != null && <div>Max guests: {c.max_guest_count}</div>}
                    {c.blocked_dates?.length > 0 && <div>Blocked: {c.blocked_dates.join(", ")}</div>}
                    {c.notes && <div className="italic">{c.notes}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <LinkCard to="/account/venue-events" icon={Users} title="All inquiries" desc="Review, call back and quote event enquiries" cta="Open" />
      </div>
    </div>
  );
}

/* ── Streaming (guided live-moment setup via /moments) ────────────── */
function StreamingPanel() {
  const [pages, setPages] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", event_date: "", location: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/moments/pages");
      setPages(data?.pages || []);
    } catch (_e) { setPages([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (form.title.trim().length < 2) { toast.error("Give your live page a title"); return; }
    setCreating(true);
    try {
      await api.post("/moments/pages", {
        title: form.title.trim(),
        event_date: form.event_date || null,
        location: form.location.trim() || null,
      });
      toast.success("Live moment page created");
      setForm({ title: "", event_date: "", location: "" });
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setCreating(false); }
  }

  function copyLink(url) {
    try { navigator.clipboard.writeText(url); toast.success("Guest link copied"); }
    catch (_e) { toast.error("Couldn't copy — copy it from the page"); }
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const hasPages = Array.isArray(pages) && pages.length > 0;
  const guided = pages != null && !hasPages;

  const createForm = (
    <form onSubmit={create} className="space-y-4 rounded-xl border border-[#E8E5DF] bg-[#FBF9F4] p-5" data-testid="biz-streaming-form">
      <Field label="Event / page title">
        <Input data-testid="biz-streaming-title" value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          placeholder="e.g. Friday Night Live at Bella Cucina" className="mt-2 rounded-xl" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Event date">
          <Input data-testid="biz-streaming-date" type="date" value={form.event_date}
            onChange={(e) => setForm((f) => ({ ...f, event_date: e.target.value }))} className="mt-2 rounded-xl" />
        </Field>
        <Field label="Location">
          <Input data-testid="biz-streaming-location" value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="Carlton VIC" className="mt-2 rounded-xl" />
        </Field>
      </div>
      <div className="flex items-center gap-3">
        <Button type="submit" data-testid="biz-streaming-create" disabled={creating}
          className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6 h-11">
          <Radio className="h-4 w-4 mr-2" strokeWidth={1.5} />
          {creating ? "Creating…" : "Create live page"}
        </Button>
        {hasPages && (
          <button type="button" onClick={() => setShowForm(false)} className="text-sm text-[#5C625E] underline">Cancel</button>
        )}
      </div>
    </form>
  );

  return (
    <div data-testid="biz-panel-streaming" className="max-w-2xl">
      <PanelHeader
        title="Streaming"
        subtitle="Spin up a QR-powered live page — guests upload photos & videos that play on your big screen."
      />

      {pages == null && <p className="text-[#5C625E]" data-testid="biz-streaming-loading">Loading…</p>}

      {guided && (
        <div className="space-y-4">
          <ol className="flex flex-wrap gap-3 text-xs text-[#5C625E]">
            <li className="flex items-center gap-1.5"><span className="h-5 w-5 rounded-full bg-[#C05746] text-white flex items-center justify-center text-[10px] font-bold">1</span> Name your page</li>
            <li className="flex items-center gap-1.5"><span className="h-5 w-5 rounded-full bg-[#F1F4EA] text-[#5A7247] flex items-center justify-center text-[10px] font-bold">2</span> Share the QR / guest link</li>
            <li className="flex items-center gap-1.5"><span className="h-5 w-5 rounded-full bg-[#F1F4EA] text-[#5A7247] flex items-center justify-center text-[10px] font-bold">3</span> Play the live slideshow</li>
          </ol>
          {createForm}
        </div>
      )}

      {hasPages && (
        <div className="space-y-4">
          {showForm ? createForm : (
            <Button type="button" data-testid="biz-streaming-new" onClick={() => setShowForm(true)}
              variant="outline" className="rounded-full border-[#E8E5DF]">
              <Plus className="h-4 w-4 mr-2" strokeWidth={1.75} /> New live page
            </Button>
          )}
          <div className="space-y-3" data-testid="biz-streaming-pages">
            {pages.map((p) => {
              const guestUrl = `${origin}/m/${p.slug}`;
              const liveUrl = `${origin}/m/${p.slug}/live`;
              return (
                <div key={p.page_id} data-testid={`biz-streaming-page-${p.slug}`}
                  className="rounded-xl border border-[#E8E5DF] p-4 bg-white">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-[#121614] truncate">{p.title}</p>
                      <p className="text-xs text-[#5C625E] mt-0.5">
                        {p.media_approved ?? 0} live · {p.media_total ?? 0} uploaded
                        {p.event_date ? ` · ${p.event_date}` : ""}
                      </p>
                    </div>
                    <span className={`shrink-0 text-[10px] uppercase tracking-wide px-2 py-1 rounded-full ${p.active ? "bg-[#E5F4EC] text-[#2F6B46]" : "bg-[#F0EBE1] text-[#8A908B]"}`}>
                      {p.active ? "Live" : "Off"}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <a href={liveUrl} target="_blank" rel="noreferrer" data-testid={`biz-streaming-slideshow-${p.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full bg-[#C05746] text-white px-3 py-1.5">
                      <Play className="h-3.5 w-3.5" strokeWidth={2} /> Open slideshow
                    </a>
                    <a href={guestUrl} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-[#E8E5DF] px-3 py-1.5 text-[#121614]">
                      <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} /> Guest page
                    </a>
                    <button type="button" onClick={() => copyLink(guestUrl)} data-testid={`biz-streaming-copy-${p.slug}`}
                      className="inline-flex items-center gap-1.5 text-xs font-medium rounded-full border border-[#E8E5DF] px-3 py-1.5 text-[#121614]">
                      <Copy className="h-3.5 w-3.5" strokeWidth={1.75} /> Copy link
                    </button>
                    <Link to={`/moments/pages/${p.page_id}`} className="inline-flex items-center gap-1 text-xs font-medium text-[#C05746] px-2 py-1.5">
                      Manage <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.75} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Team (business-scoped staff invites via /business/members) ───── */
function MembersPanel() {
  const [members, setMembers] = useState(null);
  const [you, setYou] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/business/members");
      setMembers(data?.members || []);
      setYou((data?.you || "").toLowerCase());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setMembers([]);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function invite(e) {
    e.preventDefault();
    if (!form.email.trim() || form.password.length < 8) {
      toast.error("Enter an email and an 8+ character password");
      return;
    }
    setSaving(true);
    try {
      await api.post("/business/members", {
        name: form.name.trim(), email: form.email.trim(), password: form.password,
      });
      toast.success(`${form.email.trim()} can now sign in to this business`);
      setForm({ name: "", email: "", password: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  }

  async function remove(email) {
    if (!window.confirm(`Remove ${email} from this business?`)) return;
    try {
      await api.delete(`/business/members/${encodeURIComponent(email)}`);
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div data-testid="biz-panel-team" className="max-w-2xl">
      <PanelHeader
        title="Team"
        subtitle="Invite staff to help run this business. Each gets their own sign-in with full access to your suite."
      />

      <form onSubmit={invite} className="rounded-xl border border-[#E8E5DF] bg-[#FBF9F4] p-5 space-y-4" data-testid="biz-team-invite-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name">
            <Input data-testid="biz-team-name" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="Jess from front of house" className="mt-2 rounded-xl" />
          </Field>
          <Field label="Email">
            <Input data-testid="biz-team-email" type="email" value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="staff@yourbusiness.com" className="mt-2 rounded-xl" />
          </Field>
        </div>
        <Field label="Temporary password" hint="At least 8 characters. Share it with your staff member to sign in.">
          <Input data-testid="biz-team-password" type="text" value={form.password}
            onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
            placeholder="Minimum 8 characters" className="mt-2 rounded-xl" />
        </Field>
        <Button type="submit" data-testid="biz-team-invite" disabled={saving}
          className="bg-[#C05746] hover:bg-[#A64A3C] text-white rounded-full px-6 h-11">
          <UserPlus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          {saving ? "Inviting…" : "Invite member"}
        </Button>
      </form>

      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.12em] font-bold text-[#9BA19C] mb-2">Team members</p>
        {members == null && <p className="text-sm text-[#5C625E]">Loading…</p>}
        {members && members.length === 0 && (
          <p className="text-sm text-[#5C625E]" data-testid="biz-team-empty">No staff yet — invite your first team member above.</p>
        )}
        {members && members.length > 0 && (
          <div className="space-y-2" data-testid="biz-team-list">
            {members.map((m) => {
              const isYou = (m.email || "").toLowerCase() === you;
              return (
                <div key={m.email} data-testid={`biz-team-member-${m.email}`}
                  className="flex items-center justify-between gap-4 p-3 rounded-xl border border-[#E8E5DF] bg-white">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="h-9 w-9 rounded-full bg-[#F1F4EA] text-[#5A7247] flex items-center justify-center font-bold shrink-0">
                      {(m.name || m.email || "?").slice(0, 1).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-[#121614] truncate">
                        {m.name}{isYou && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#5A7247]">You</span>}
                      </p>
                      <p className="text-xs text-[#5C625E] truncate flex items-center gap-1">
                        <Mail className="h-3 w-3" strokeWidth={1.75} />{m.email}
                      </p>
                    </div>
                  </div>
                  {!isYou && (
                    <button type="button" data-testid={`biz-team-remove-${m.email}`} onClick={() => remove(m.email)}
                      className="shrink-0 inline-flex items-center gap-1 text-xs text-[#C05746] hover:bg-[#C05746]/10 rounded-full px-3 py-1.5 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} /> Remove
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Chat ─────────────────────────────────────────────────────────── */
function ChatPanel() {
  return (
    <div data-testid="biz-panel-chat" className="max-w-2xl">
      <PanelHeader
        title="Chat"
        subtitle="Message customers and respond to enquiries in real time."
      />
      <div className="space-y-2">
        <LinkCard to="/hustlechat" icon={MessageCircle} title="Open HustleChat" desc="Your conversation inbox" cta="Open" />
      </div>
    </div>
  );
}

export default BusinessSettingsHub;
